import React, {useState, useEffect} from 'react';

import SideNavigation from "@amzn/awsui-components-react/polaris/side-navigation";
import { Icon } from "@amzn/awsui-components-react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from '../utilities/adminUtils';
import { getOwnersByUser, getOwnersForAdminUser } from "src/utilities/CloseAPIs";
import { configureGenericAppSync } from "src/context/AuthContextUtility";

const SideNavItem = ({children}) => {
  return (
    <div className="sidenav-item">{children}</div>
  )
}

export default (props) => {
  const [activeHref, setActiveHref] = useState(props.activeHref);
  const history = useHistory();

  // Self-fetch cpOwnerOptions when not provided as props
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);
  const isPortalAdmin = useSelector(selectIsEffectiveAdmin);
  const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin);
  const [internalCpOwnerOptions, setInternalCpOwnerOptions] = useState(null);

  const cpOwnerOptions = props.cpOwnerOptions || internalCpOwnerOptions;

  useEffect(() => {
    // Only self-fetch if cpOwnerOptions is not provided as a prop
    if (props.cpOwnerOptions) return;
    if (!currentStage) return;

    const fetchOwners = async () => {
      if (isPortalAdmin || isSupportAdmin) {
        await getOwnersForAdminUser(
          function onSuccess(result) {
            const data = result.data.getOwnersForAdminUserRDS;
            const owners = JSON.parse(data);
            const ownerSelectOptions = owners.map((owner) => ({
              label: owner?.owner_name,
              value: String(owner?.owner_id),
              owner_identifier: owner?.owner_identifier,
              owner_type: owner?.owner_type
            }));
            setInternalCpOwnerOptions(ownerSelectOptions);
            configureGenericAppSync();
          },
          function onFailure() {
            configureGenericAppSync();
          }
        );
      } else {
        await getOwnersByUser(currentStage,
          function onSuccess(result) {
            const data = result.data.getOwnersByUser;
            const owners = JSON.parse(data);
            const ownerSelectOptions = owners.map((owner) => ({
              label: owner?.owner_name,
              value: String(owner?.owner_id),
              owner_identifier: owner?.owner_identifier,
              owner_type: owner?.owner_type
            }));
            setInternalCpOwnerOptions(ownerSelectOptions);
            configureGenericAppSync();
          },
          function onFailure() {
            configureGenericAppSync();
          }
        );
      }
    };

    fetchOwners();
  }, [currentStage, isPortalAdmin, isSupportAdmin, props.cpOwnerOptions]);

  const getSideNavItems = (currentActiveHref) => {
    // Look up team IDs from DB-fetched owner options
    const corporateReporting = cpOwnerOptions?.find(opt => opt.label === 'Corporate Reporting');
    const statutoryAccounts = cpOwnerOptions?.find(opt => opt.label === 'Statutory Close');
    const journalEntry = cpOwnerOptions?.find(opt => opt.label === 'Journal Entry');
    const reconciliation = cpOwnerOptions?.find(opt => opt.label === 'Reconciliation');
    const policy = cpOwnerOptions?.find(opt => opt.label === 'Policy');
    const acResources = cpOwnerOptions?.find(opt => opt.label === 'AC Resources');
    const ateamResources = cpOwnerOptions?.find(opt => opt.label === 'ATEAM Resources');

    const myTeamsHref = (props.homeCPView?.value || props.selectedCPView?.value) ? `/ClosePortal/HOME/Page/${props.homeCPView?.value || props.selectedCPView?.value}` : "/ClosePortal/HOME";
    const corpReportingHref = corporateReporting ? `/ClosePortal/Teams/${corporateReporting.value}` : "/ClosePortal/Teams/not-found-corporate-reporting";
    const statutoryHref = statutoryAccounts ? `/ClosePortal/Teams/${statutoryAccounts.value}` : "/ClosePortal/Teams/not-found-statutory-close";
    const journalEntryHref = journalEntry ? `/ClosePortal/Teams/${journalEntry.value}` : "/ClosePortal/Teams/not-found-journal-entry";
    const reconciliationHref = reconciliation ? `/ClosePortal/Teams/${reconciliation.value}` : "/ClosePortal/Teams/not-found-reconciliation";
    const policyHref = policy ? `/ClosePortal/Teams/${policy.value}` : "/ClosePortal/Teams/not-found-policy";
    const acResourcesHref = acResources ? `/ClosePortal/Teams/${acResources.value}` : "/ClosePortal/Teams/not-found-ac-resources";
    const ateamResourcesHref = ateamResources ? `/ClosePortal/Teams/${ateamResources.value}` : "/ClosePortal/Teams/not-found-ateam-resources";

    const items = [
          ...(props.appName && props.appHref ? [
            {
              "type": "link",
              "text": <SideNavItem href={props.appHref} activeHref={currentActiveHref}><Icon name="view-full" /> {props.appName}</SideNavItem>,
              "href": props.appHref
            },
            { "type": "divider" },
          ] : []),
          {
            "type": "link",
            "text": <SideNavItem href={myTeamsHref} activeHref={currentActiveHref}><Icon name="folder" /> My Team(s)</SideNavItem>,
            "href": myTeamsHref
          },
          { "type": "divider" },
          {
            "type": "section",
            "text": <span style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '0.03em' }}>Resources</span>,
            "items": [
              {
                "type": "link",
                "text": <SideNavItem href="/AppsLibrary" activeHref={currentActiveHref}><Icon name="gen-ai" /> Application Repository</SideNavItem>,
                "href": "/AppsLibrary"
              },
              {
                "type": "link",
                "text": <SideNavItem href={corpReportingHref} activeHref={currentActiveHref}><Icon name="file" /> Corporate Reporting</SideNavItem>,
                "href": corpReportingHref
              },
              {
                "type": "link",
                "text": <SideNavItem href={statutoryHref} activeHref={currentActiveHref}><Icon name="file-open" /> Statutory Close</SideNavItem>,
                "href": statutoryHref
              },
              {
                "type": "link",
                "text": <SideNavItem href={journalEntryHref} activeHref={currentActiveHref}><Icon name="mini-player" /> Journal Entry</SideNavItem>,
                "href": journalEntryHref
              },
              {
                "type": "link",
                "text": <SideNavItem href={reconciliationHref} activeHref={currentActiveHref}><Icon name="check" /> Reconciliation</SideNavItem>,
                "href": reconciliationHref
              },
              {
                "type": "link",
                "text": <SideNavItem href={policyHref} activeHref={currentActiveHref}><Icon name="security" /> Policy</SideNavItem>,
                "href": policyHref
              },
              {
                "type": "link",
                "text": <SideNavItem href={acResourcesHref} activeHref={currentActiveHref}><Icon name="folder-open" /> AC Resources</SideNavItem>,
                "href": acResourcesHref
              },
              {
                "type": "link",
                "text": <SideNavItem href={ateamResourcesHref} activeHref={currentActiveHref}><Icon name="support" /> A-TEAM Resources</SideNavItem>,
                "href": ateamResourcesHref
              },
              {
                "type": "link",
                "text": <SideNavItem href="/ClosePortal/AIDecisionTree" activeHref={currentActiveHref}><Icon name="share" /> AI Decision Tree</SideNavItem>,
                "href": "/ClosePortal/AIDecisionTree"
              },
              {
                "type": "link",
                "text": <SideNavItem href="/ClosePortal/ATEAMMCPExplorer" activeHref={currentActiveHref}><Icon name="search" /> ATEAM MCP Explorer</SideNavItem>,
                "href": "/ClosePortal/ATEAMMCPExplorer"
              },
              {
                "type": "link",
                "text": <SideNavItem href="/taskmanager" activeHref={currentActiveHref}><Icon name="grid-view" /> Task Management</SideNavItem>,
                "href": "/taskmanager"
              },
            ]
          }
        ]

    return items;
  }

  const [items, setItems] = useState(getSideNavItems(activeHref));

  useEffect(() => {
    setItems(getSideNavItems(activeHref));
  }, [cpOwnerOptions, props.selectedCPView, props.homeCPView, activeHref, props.appName, props.appHref])

  useEffect(() => {
    if (props.activeHref === "/AppsLibrary") {
      setActiveHref("/AppsLibrary");
    } else if (/^\/ClosePortal\/Teams\/\d+/.test(props.activeHref)) {
      // Match the team link in sidenav (without query params)
      const teamId = props.activeHref.match(/\/ClosePortal\/Teams\/(\d+)/)?.[1];
      if (teamId) {
        setActiveHref(`/ClosePortal/Teams/${teamId}`);
      } else {
        setActiveHref(props.activeHref);
      }
    } else if (/^\/ClosePortal\/.+\/Page\/.+$/.test(props.activeHref)) {
      setActiveHref(props.selectedCPView?.value ? `/ClosePortal/HOME/Page/${props.selectedCPView.value}` : "/ClosePortal/HOME");
    } else {
      setActiveHref(props.activeHref);
    }
  }, [props.activeHref, props.selectedCPView])

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{"text": "Workspace"}}
      onFollow={e => {
        e.stopPropagation();
        e.preventDefault();
        const href = e.detail.href;
        
        if (href) {
          history.push(href);
        }
        setActiveHref(href);
      }}
      items={items}
    />
  );
}
